import {
  hasOneOfGlobalRoles,
  hasOneOfOrgRoles,
  Role,
  AuthUser,
} from '../../contexts/AuthProvider/types';
import { MedisanteDevice, DeviceModels } from '../../pages/Device/types';
import { IDialogState, IActionItemState } from './types';

export const getActionItemsState = (
  authUser: AuthUser,
  devices: MedisanteDevice[]
): IActionItemState => {
  const initialActionItemsState: IActionItemState = {
    showChangeTimeZone: devices.reduce(
      (result: boolean, d: MedisanteDevice) => !!result && !!d.timeZone,
      true
    ),
    showResetToken: devices.reduce(
      (result: boolean, d: MedisanteDevice) => result && !!d.token,
      true
    ),
    showTransferOwnership: hasTransferOwnershipPermissions(authUser),
    showChangeTargetSystems: true,
    showChangeWeightUnit: true,
    showChangeMinimumWeightFilter: true,
    showConfigureSync: true,
    showUnlinkDevices: false,
    canTestDevice:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
    canDeleteDevice: hasOneOfGlobalRoles(authUser, [Role.DeviceManager]),
    canMuteDevice:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
    canArchiveDevice: hasWritingPermissions(authUser, devices),
    canPinDevice:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
    canFlagDevice:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
    canUnlinkDevices:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
    canResetToken: hasWritingPermissions(authUser, devices),
    canConfigureSync: hasWritingPermissions(authUser, devices),
    canChangeMinimumWeightFilter: hasWritingPermissions(authUser, devices),
    canChangeWeightUnit:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
    canChangeTimeZone:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
    canChangeTargetSystems:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
    canResetCount:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
    canBlock:
      hasOneOfGlobalRoles(authUser, [Role.DeviceManager]) &&
      checkArchiveStatus(devices),
    canStockDevice:
      hasWritingPermissions(authUser, devices) && checkArchiveStatus(devices),
  };

  return devices.reduce((actionItemsState, device) => {
    let deviceModel = device.model;
    if (device.properties && device.properties.model) {
      // override default device model. Issue when a BP800 acts like a D40G
      deviceModel = device.properties.model.join(' ');
    }
    if (device.allowUnlinkedGateway) {
      actionItemsState.canChangeTargetSystems = false;
    }
    switch (deviceModel) {
      case DeviceModels.BC800:
        return {
          ...actionItemsState,
          showChangeWeightUnit:
            actionItemsState.showChangeWeightUnit && !!device.weightUnit,
          showChangeMinimumWeightFilter:
            actionItemsState.showChangeMinimumWeightFilter &&
            !!device.minimumWeight,
          showConfigureSync:
            actionItemsState.showConfigureSync &&
            device.version !== '1.1.4' &&
            device.version !== '1.0.0-test',
        };
      case DeviceModels.BP800:
        return {
          ...actionItemsState,
          showChangeWeightUnit: false,
          showChangeMinimumWeightFilter: false,
          showConfigureSync:
            actionItemsState.showConfigureSync && device.version === '44.0.0',
        };
      case DeviceModels.D40G:
        return {
          ...actionItemsState,
          showChangeWeightUnit: false,
          showChangeMinimumWeightFilter: false,
          showConfigureSync:
            actionItemsState.showConfigureSync &&
            ['44.0.0', '48.0.0', '49.0.0'].includes(device.version),
        };
      case DeviceModels.BT005:
      case DeviceModels.BS2001G:
      case DeviceModels.PS300CRPM:
        return {
          ...actionItemsState,
          showChangeMinimumWeightFilter: true,
          showChangeWeightUnit: false,
          showConfigureSync: false,
        };
      case DeviceModels.GW9017:
      case DeviceModels.ISGI:
        return {
          ...actionItemsState,
          showChangeMinimumWeightFilter: false,
          showChangeWeightUnit: false,
          showConfigureSync: false,
          showChangeTimeZone: true,
          showUnlinkDevices: devices.length === 1,
          canUnlinkDevices: !(device.allowUnlinkedGateway || false),
          showChangeTargetSystems: !!device.allowUnlinkedGateway,
          canResetCount: !(device.allowUnlinkedGateway || false),
          canUnlinkedGateway: true,
        };
      case DeviceModels.P30:
      case DeviceModels.IR42:
      case DeviceModels.PO200:
      case DeviceModels.W550:
      case DeviceModels.FORA6:
      case DeviceModels.BM96:
      case DeviceModels.IR20:
      case DeviceModels.P80:
      case DeviceModels.MS6110:
      case DeviceModels.AOJ25A:
      case DeviceModels.PC60NW:
        return {
          ...actionItemsState,
          showChangeMinimumWeightFilter: false,
          showChangeWeightUnit: false,
          showConfigureSync: false,
          showChangeTimeZone: false,
          showChangeTargetSystems: true,
          showTransferOwnership: hasTransferOwnershipPermissions(authUser),
          showUnlinkDevices: true,
          canResetCount: true,
        };
      case DeviceModels.LS802GP:
      case DeviceModels.BM300CRPM:
        return {
          ...actionItemsState,
          showChangeMinimumWeightFilter: false,
          showChangeWeightUnit: false,
          showConfigureSync: false,
        };
      default:
        return {
          ...actionItemsState,
          showChangeWeightUnit: false,
          showChangeMinimumWeightFilter: false,
          showConfigureSync: false,
        };
    }
  }, initialActionItemsState);
};

export const hasWritingPermissions = (
  authUser: AuthUser,
  devices: MedisanteDevice[]
) => {
  if (!authUser) {
    return false;
  }
  if (
    authUser.globalRoles &&
    authUser.globalRoles.includes(Role.DeviceManager)
  ) {
    return true;
  }

  const getRolesByDeviceOwner = (device: MedisanteDevice) => {
    const deviceOrgs =
      authUser &&
      authUser.orgs &&
      authUser.orgs.find((o) => o.id === device.owner);
    return deviceOrgs ? deviceOrgs.roles : [];
  };

  return devices
    .map(getRolesByDeviceOwner)
    .every((roles) => roles && roles.includes(Role.DeviceManager));
};

export const hasTransferOwnershipPermissions = (authUser: AuthUser) => {
  if (!authUser) {
    return false;
  }
  if (hasOneOfGlobalRoles(authUser, [Role.DeviceManager])) {
    return true;
  }
  let writableOrgCount = 0;
  for (const o of authUser.orgs) {
    if (hasOneOfOrgRoles(authUser, [o.id], [Role.DeviceManager])) {
      writableOrgCount++;
    }
  }
  if (writableOrgCount < 2) {
    return false;
  }
  return true;
};

export const dialogStateReducer = (state: IDialogState, action: string) => {
  switch (action) {
    case 'OPEN_MENU':
      return {
        isMenuOpen: true,
      };
    case 'OPEN_CHANGE_TIME_ZONE':
      return {
        isChangeTimeZoneDialogOpen: true,
        isMenuOpen: false,
      };
    case 'OPEN_CHANGE_WEIGHT_UNIT':
      return {
        isChangeWeightUnitDialogOpen: true,
        isMenuOpen: false,
      };
    case 'OPEN_WEIGHT_FILTER':
      return {
        isWeightFilterDialogVisible: true,
        isMenuOpen: false,
      };
    case 'OPEN_CONFIGURE_SYNC':
      return {
        isConfigureSyncDialogOpen: true,
        isMenuOpen: false,
      };
    case 'OPEN_RESET_TOKEN':
      return {
        isResetTokenDialogOpen: true,
        isMenuOpen: false,
      };
    case 'CHANGE_TARGET_SYSTEM':
      return {
        isChangeTargetSystemsDialogOpen: true,
        isMenuOpen: false,
      };
    case 'TRANSFER_OWNERSHIP':
      return {
        isTransferOwnershipDialogOpen: true,
        isMenuOpen: false,
      };
    case 'DEVICE_LINKING':
      return {
        isDeviceLinkingDialogOpen: true,
        isMenuOpen: false,
      };
    case 'FLAG_DEVICE':
      return {
        isFlagDeviceDialogOpen: true,
        isMenuOpen: false,
      };
    case 'PIN_DEVICE':
      return {
        isPinDialogOpen: true,
        isMenuOpen: false,
      };
    case 'ARCHIVE_DEVICE':
      return {
        isArchiveDialogOpen: true,
        isMenuOpen: false,
      };
    case 'DELETE_DEVICE':
      return {
        isDeleteDeviceDialogOpen: true,
        isMenuOpen: false,
      };
    case 'MUTE_DEVICE':
      return {
        isMuteDialogOpen: true,
        isMenuOpen: false,
      };
    case 'RESET_COUNT':
      return {
        isResetCountDialogOpen: true,
        isMenuOpen: false,
      };
    case 'BLOCK_DEVICE':
      return {
        isBlockDeviceDialogOpen: true,
        isMenuOpen: false,
      };
    case 'CLOSE_MENU':
    case 'CLOSE_DIALOG':
    default:
      return {
        isMenuOpen: false,
        isDeviceLinkingDialogOpen: false,
      };
  }
};

export const checkArchiveStatus = (d: MedisanteDevice[]) => {
  return d.some((i) => {
    if (i.isArchived === undefined) {
      return true;
    }
    return !i.isArchived;
  });
};
